import type {
  HabitaProcessListItem,
  KanbanColumnData,
  PastaStatus,
} from '../../shared/models';
import {
  PASTA_STATUS_DESCRIPTIONS,
  PASTA_STATUS_LABELS,
  pastaStatusToApiStatus,
} from '../../shared/habitaStatusPresentation';
import type { ProcessSortOrder } from '../../shared/utils/processOrdering';
import { ProcessRepository } from './ProcessRepository';

export interface KanbanColumnPageParams {
  status: PastaStatus;
  page: number;
  page_size: number;
  searchText?: string;
  processoText?: string;
  responsibleEmail?: string;
  startDate?: string;
  endDate?: string;
  sortOrder?: ProcessSortOrder;
}

export interface KanbanColumnPageResult {
  column: KanbanColumnData;
  items: HabitaProcessListItem[];
  page: number;
  total: number;
  hasExplicitTotal: boolean;
  hasNextPage: boolean;
}

function createEmptyColumn(status: PastaStatus): KanbanColumnData {
  return {
    status,
    label: PASTA_STATUS_LABELS[status],
    description: PASTA_STATUS_DESCRIPTIONS[status],
    items: [],
  };
}

function loadedCountLowerBound(
  params: Pick<KanbanColumnPageParams, 'page' | 'page_size'>,
  itemCount: number
): number {
  return ((params.page - 1) * params.page_size) + itemCount;
}

export class KanbanRepository {
  private processRepository = new ProcessRepository();

  async fetchKanbanColumnPage(params: KanbanColumnPageParams): Promise<KanbanColumnPageResult> {
    const emptyColumn = createEmptyColumn(params.status);
    const processStatus = pastaStatusToApiStatus(params.status);
    if (!processStatus) {
      return {
        column: emptyColumn,
        items: [],
        page: params.page,
        total: 0,
        hasExplicitTotal: true,
        hasNextPage: false,
      };
    }

    const searchText = params.searchText?.trim();
    const processoText = params.processoText?.trim();
    const responsibleEmail = params.responsibleEmail?.trim();
    const result = await this.processRepository.findAllWithCustomer({
      page: params.page,
      page_size: params.page_size,
      status: processStatus,
      ...(searchText ? { searchText } : {}),
      ...(processoText ? { processoText } : {}),
      ...(responsibleEmail ? { responsibleEmail } : {}),
      ...(params.startDate ? { startDate: params.startDate } : {}),
      ...(params.endDate ? { endDate: params.endDate } : {}),
      ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
    });
    const items = result.items;
    const hasExplicitTotal = Boolean(result.hasExplicitTotal);
    const hasNextPage = hasExplicitTotal
      ? params.page * params.page_size < result.total
      : items.length === params.page_size;

    return {
      column: {
        ...emptyColumn,
        items,
      },
      items,
      page: params.page,
      total: hasExplicitTotal ? result.total : loadedCountLowerBound(params, items.length),
      hasExplicitTotal,
      hasNextPage,
    };
  }

}
